import { bool, func, shape, string } from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';
import PopupDialog from '../../../dialogs/PopupDialog';
import MonitoringDeviceDiagnosticsSection from './MonitoringDeviceDiagnosticsSection';

const DIALOG_ID = 'infrastructure_log-dialog';
const OK_BUTTON_TEXT = 'OK';

const INITIAL_OPTIONS = {};

const optionsType = shape({
    ipAddress: string,
    _id: string,
});

/**
 * Custom hook to manage Classifier log dialog state
 * @returns {Array} [dialog component, showDialog function]
 */
export const useClassifierLogDialog = () => {
    const [open, setOpen] = useState(false);
    const [infrlogOptions, setInfrlogOptions] = useState(INITIAL_OPTIONS);

    const showDialog = useCallback((newInfrlogOptions) => {
        if (!newInfrlogOptions?._id) {
            return;
        }
        setInfrlogOptions(newInfrlogOptions);
        setOpen(true);
    }, []);

    const dialog = useMemo(
        () => <ClassifierLogDialog open={open} setOpen={setOpen} infrlogOptions={infrlogOptions} />,
        [open, infrlogOptions]
    );

    return [dialog, showDialog];
};

/**
 * Internal dialog component that renders the infrastructure log popup
 * @param {Object} props - Component props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.setOpen - Function to set dialog open state
 * @param {Object} props.infrlogOptions - Infrastructure log options
 * @param {string} props.infrlogOptions.ipAddress - Device IP address
 * @param {string} props.infrlogOptions._id - Device ID
 * @param {string} props.infrlogOptions.uiState - Device uiState
 * @param {string} props.infrlogOptions.deviceType - Device deviceType
 * @returns {JSX.Element} Popup dialog with classifier log table
 */
const ClassifierLogDialogContent = ({ open, setOpen, infrlogOptions }) => {
    const { ipAddress, _id, uiState, deviceType } = infrlogOptions;

    const handleClose = useCallback(() => {
        setOpen(false);
    }, [setOpen]);

    const dialogTitle = ipAddress ? `Monitoring Diagnostics - ${ipAddress}` : 'Monitoring Diagnostics';

    return (
        <PopupDialog
            dialogId={DIALOG_ID}
            title={dialogTitle}
            onCancel={handleClose}
            open={open}
            buttonText={OK_BUTTON_TEXT}
            showCloseButton
        >
            <MonitoringDeviceDiagnosticsSection id={_id} uiState={uiState} deviceType={deviceType} showMessage />
        </PopupDialog>
    );
};

ClassifierLogDialogContent.propTypes = {
    open: bool.isRequired,
    setOpen: func.isRequired,
    infrlogOptions: optionsType.isRequired,
};

/**
 * Classifier log dialog component with validation
 * @param {Object} props - Component props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.setOpen - Function to set dialog open state
 * @param {Object} props.infrlogOptions - Infrastructure log options
 * @returns {JSX.Element|null} Dialog component or null if device ID is missing
 */
const ClassifierLogDialog = ({ open, setOpen, infrlogOptions }) => {
    const { _id } = infrlogOptions;

    if (isEmpty(_id)) {
        return null;
    }

    return <ClassifierLogDialogContent open={open} setOpen={setOpen} infrlogOptions={infrlogOptions} />;
};

ClassifierLogDialog.propTypes = {
    open: bool.isRequired,
    setOpen: func.isRequired,
    infrlogOptions: optionsType.isRequired,
};

export default ClassifierLogDialog;
